import { useEffect, useState } from 'react';

/**
 * Debounce hook that delays updating a value until after a specified delay
 *
 * Why: To prevent excessive re-renders and improve performance by delaying updates
 * until user input has stopped for a specified duration.
 *
 * How: Uses setTimeout internally to delay updating the value until after the
 * specified delay period. Cleans up the timeout on unmount.
 *
 * Usage Patterns:
 * - Debounce input changes: `const debouncedValue = useDebounce(value, 200)`
 * - Debounce button clicks: `const debouncedClick = useDebounce(onClick, 100)`
 * - Debounce API calls: `const debouncedFetch = useDebounce(fetchData, 500)`
 */
export const useDebounce = <T>(value: T, delay: number): T => {
	const [debouncedValue, setDebouncedValue] = useState<T>(value);

	useEffect(() => {
		const handler = setTimeout(() => {
			setDebouncedValue(value);
		}, delay);

		return () => {
			clearTimeout(handler);
		};
	}, [value, delay]);

	return debouncedValue;
};
